// WindowActivationButtonDemo.jsx
import React, { useState, useEffect } from "react";
import WindowActivationButton from "../../Shared/WindowActivationButton";
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const WindowActivationButtonDemo = () => {
  const [participantId, setParticipantId] = useState('');
  const [participantInfo, setParticipantInfo] = useState(null);
  const [availableWindows, setAvailableWindows] = useState([]);
  const [selectedWindows, setSelectedWindows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activate, setActivate] = useState(true);
  const [result, setResult] = useState(null);

  // Parse participant ID from URL if present
  useEffect(() => {
    const parseParticipantFromUrl = () => {
      const path = window.location.pathname;
      const regex = /\/participant\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
      const match = path.match(regex);
      
      if (match && match[1]) {
        setParticipantId(match[1]);
        fetchParticipantWindows(match[1]);
      }
    };
    
    parseParticipantFromUrl();
  }, []);

  const fetchParticipantWindows = async (id) => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      // Get participant info
      const infoResponse = await axios.get(`/api/participants/${id}`);
      setParticipantInfo(infoResponse.data);
      
      // Get available windows for activation
      const windowsResponse = await axios.get(`/api/participants/${id}/all-surveys-for-activation/`);
      setAvailableWindows(windowsResponse.data || []);
      setResult(null);
    } catch (error) {
      console.error('Error fetching participant data:', error);
      toast.error('Failed to fetch participant windows');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuccess = (data) => {
    setResult({ success: true, data });
    // Refresh window data after activation/deactivation
    fetchParticipantWindows(participantId);
    toast.success(`Windows ${activate ? 'activated' : 'deactivated'} successfully`);
  };

  const handleError = (error) => {
    setResult({ success: false, error: error.message });
    console.error('Error:', error);
    toast.error(`Error ${activate ? 'activating' : 'deactivating'} windows`);
  };
  
  const toggleWindowSelection = (windowId) => {
    setSelectedWindows(prevSelected => {
      if (prevSelected.includes(windowId)) {
        return prevSelected.filter(id => id !== windowId);
      } else {
        return [...prevSelected, windowId];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (selectedWindows.length === availableWindows.length) {
      setSelectedWindows([]);
    } else {
      setSelectedWindows(availableWindows.map(window => window.id));
    }
  };

  const notice = (tone, children) => (
    <div
      className={`rounded-lg border px-4 py-3 text-sm ${
        tone === 'warning'
          ? 'border-warning-500/30 bg-warning-50 text-warning-800 dark:bg-warning-500/10 dark:text-warning-400'
          : 'border-info-500/30 bg-info-50 text-info-800 dark:bg-info-500/10 dark:text-info-400'
      }`}
    >
      {children}
    </div>
  );

  return (
    <div className="mb-8 mt-4">
      <div className="card overflow-hidden">
        <div className="card-header">
          <h4 className="card-title">Survey Window Management</h4>
        </div>

        <div className="card-body space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2">
              <label htmlFor="window-demo-participant" className="label">Participant UUID</label>
              <div className="flex gap-2">
                <input
                  id="window-demo-participant"
                  type="text"
                  className="input"
                  value={participantId}
                  onChange={(e) => setParticipantId(e.target.value)}
                  placeholder="Enter participant UUID..."
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => fetchParticipantWindows(participantId)}
                  disabled={!participantId || isLoading}
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" aria-label="Loading" /> : 'Load'}
                </button>
              </div>
              <p className="hint">Format: 8d96a329-6761-44c6-a6e4-fa0457ebdbcc</p>
            </div>
            <fieldset>
              <legend className="label">Action</legend>
              <div className="flex flex-wrap gap-4 pt-2 text-sm text-gray-700 dark:text-gray-300">
                <label className="inline-flex items-center gap-2">
                  <input type="radio" id="activate" checked={activate} onChange={() => setActivate(true)} className="accent-primary-600" />
                  Activate Windows
                </label>
                <label className="inline-flex items-center gap-2">
                  <input type="radio" id="deactivate" checked={!activate} onChange={() => setActivate(false)} className="accent-primary-600" />
                  Deactivate Windows
                </label>
              </div>
            </fieldset>
          </div>

          {participantInfo && notice('info', (
            <div className="grid gap-1 md:grid-cols-2">
              <div><strong>ID:</strong> {participantInfo.participant_id || 'N/A'}</div>
              <div><strong>Subject:</strong> {participantInfo.subject_id || 'N/A'}</div>
              <div><strong>Site:</strong> {participantInfo.site?.name || 'N/A'}</div>
              <div><strong>Study:</strong> {participantInfo.site?.study?.name || 'N/A'}</div>
            </div>
          ))}

          {isLoading ? (
            <div className="my-10 flex flex-col items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
              Loading participant data...
            </div>
          ) : availableWindows.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <h5 className="section-title">Available Survey Windows</h5>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleSelectAll}>
                  {selectedWindows.length === availableWindows.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <ul className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {availableWindows.map((window) => {
                  const selected = selectedWindows.includes(window.id);
                  return (
                    <li key={window.id}>
                      <button
                        type="button"
                        onClick={() => toggleWindowSelection(window.id)}
                        aria-pressed={selected}
                        className={`flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors ${
                          selected ? 'bg-primary-50 dark:bg-primary-400/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                        }`}
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{window.survey.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Window: {new Date(window.window.start_time).toLocaleString()} - {new Date(window.window.end_time).toLocaleString()}
                          </div>
                        </div>
                        <span className={`badge ${window.window.status === 'open' ? 'badge-success' : 'badge-gray'}`}>
                          {window.window.status}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="flex justify-center">
                <WindowActivationButton
                  participantId={participantId}
                  windowIds={selectedWindows}
                  activate={activate}
                  onSuccess={handleSuccess}
                  onError={handleError}
                  disabled={selectedWindows.length === 0}
                  variant={activate ? "primary" : "danger"}
                  size="md"
                  customLabel={`${activate ? 'Activate' : 'Deactivate'} Selected Windows (${selectedWindows.length})`}
                />
              </div>
            </>
          ) : participantId ? (
            notice('warning', 'No survey windows available for this participant. Please check if the participant has any assigned surveys.')
          ) : (
            notice('info', 'Enter a participant UUID and click "Load" to view available survey windows.')
          )}

          {result && (
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
              <div className={`px-4 py-2 text-sm font-medium ${result.success ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400' : 'bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-400'}`}>
                Operation Result
              </div>
              <pre className="max-h-[200px] overflow-auto p-4 text-xs text-gray-700 dark:text-gray-300">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WindowActivationButtonDemo;